/**
 * Publishes the Shopify analytics custom event "PortraitGeneriert" whenever a
 * portrait generation is started. A Custom Pixel (Admin → Kundenereignisse)
 * subscribes to it and forwards it to Meta.
 *
 * Passive listener only – never touches the upload/generation flow. Every
 * failure is swallowed so tracking can never break generation.
 *
 * - Fires only with marketing consent (Shopify Customer Privacy API)
 * - Fires once per sessionId + motiv + set of uploaded photos (remembered in
 *   localStorage) – new photos for the same motiv count as a new generation
 * - No personal data: sessionId is only used as a SHA-256 hash (event_id)
 */
(() => {
  const EVENT_NAME = 'PortraitGeneriert';
  const TRACKED_KEY = 'kunstpfote_tracked_generations';
  const UPLOAD_DATA_KEY = 'kunstpfote_uploads';

  const readTracked = () => {
    try {
      const list = JSON.parse(localStorage.getItem(TRACKED_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  };

  const markTracked = (eventId) => {
    try {
      const list = readTracked();
      list.push(eventId);
      localStorage.setItem(TRACKED_KEY, JSON.stringify(list.slice(-50)));
    } catch {}
  };

  // Sorted so the order in which photos were added doesn't matter
  const readUploadedFiles = () => {
    try {
      const originals = JSON.parse(localStorage.getItem(UPLOAD_DATA_KEY) || '{}').originals;
      return Array.isArray(originals) ? [...originals].sort() : [];
    } catch {
      return [];
    }
  };

  const marketingAllowed = () => {
    try {
      return Boolean(window.Shopify?.customerPrivacy?.marketingAllowed?.());
    } catch {
      return false;
    }
  };

  const sha256 = async (text) => {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('');
  };

  // Prevents a double publish if two clicks arrive before the hash resolves
  const inFlight = new Set();

  const track = async ({ sessionId, sku }) => {
    const motivId = (sku || '').split('-').slice(0, 2).join('-');
    if (!sessionId || !motivId) return;
    if (!marketingAllowed()) return;
    if (typeof window.Shopify?.analytics?.publish !== 'function') return;

    const files = readUploadedFiles().join(',');
    const eventId = `pg_${(await sha256(`${sessionId}|${motivId}|${files}`)).slice(0, 32)}`;
    if (inFlight.has(eventId) || readTracked().includes(eventId)) return;
    inFlight.add(eventId);

    const payload = { event_id: eventId, motiv_id: motivId };
    const motivName = document.querySelector('[data-motiv-name]')?.dataset.motivName?.trim();
    if (motivName) payload.motiv_name = motivName;

    window.Shopify.analytics.publish(EVENT_NAME, payload);
    markTracked(eventId);
  };

  window.addEventListener('kunstpfote:generation-started', (e) => {
    try {
      track(e.detail || {}).catch(() => {});
    } catch {}
  });
})();
